import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FormField } from "@/components/shared/FormField";
import { ContactFormSuccessPanel } from "@/components/shared/ContactFormSuccessPanel";
import { apiClient } from "@/lib/api";
import { CASE_TYPE_OPTIONS } from "@/constants/firmInfo";
import { useLanguage } from "@/i18n/LanguageContext";

const EMPTY_FORM = { full_name: "", phone: "", email: "", case_type: "", message: "" };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

// Loads a reCAPTCHA v3 token just-in-time for this submission. Resolves to
// null (rather than throwing) if the script hasn't loaded yet, so a slow
// network never blocks a genuine lead from submitting.
const getRecaptchaToken = (action) =>
  new Promise((resolve) => {
    if (!RECAPTCHA_SITE_KEY || !window.grecaptcha) {
      resolve(null);
      return;
    }
    try {
      window.grecaptcha.ready(() => {
        window.grecaptcha
          .execute(RECAPTCHA_SITE_KEY, { action })
          .then(resolve)
          .catch(() => resolve(null));
      });
    } catch {
      resolve(null);
    }
  });

const validateContactForm = (form, t) => {
  const next = {};
  if (!form.full_name.trim()) next.full_name = t("contactForm.errors.fullName");
  if (!form.phone.trim() || form.phone.trim().length < 7) next.phone = t("contactForm.errors.phone");
  if (!form.email.trim() || !EMAIL_RE.test(form.email.trim())) next.email = t("contactForm.errors.email");
  if (!form.case_type) next.case_type = t("contactForm.errors.caseType");
  if (!form.message.trim()) next.message = t("contactForm.errors.message");
  return next;
};

/**
 * Reusable lead-capture form.
 * variant="compact" -> shorter layout for the hero "Instant Case Evaluation" dialog.
 * testIdPrefix -> lets callers namespace testids ("contact" | "case-evaluation").
 */
export const ContactForm = ({
  variant = "full",
  source = "contact_page",
  testIdPrefix = "contact",
  onSuccess,
}) => {
  const { t, lang } = useLanguage();
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState("");

  const setField = (key) => (e) => {
    const value = typeof e === "string" ? e : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError("");
    const validationErrors = validateContactForm(form, t);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    setSubmitting(true);
    try {
      const recaptchaToken = await getRecaptchaToken("contact_submit");
      await apiClient.post("/leads", { ...form, source, language: lang, recaptcha_token: recaptchaToken });
      setSubmitted(true);
      toast.success(t("contactForm.toastSuccess"));
      onSuccess && onSuccess();
    } catch (err) {
      const detail = err?.response?.data?.detail || t("contactForm.errors.generic");
      setServerError(detail);
      toast.error(t("contactForm.toastError"));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <ContactFormSuccessPanel
        testIdPrefix={testIdPrefix}
        heading={t("contactForm.successHeading")}
        body={t("contactForm.successBody")}
        submitAnotherLabel={t("contactForm.submitAnother")}
        onReset={() => {
          setForm(EMPTY_FORM);
          setSubmitted(false);
        }}
      />
    );
  }

  const fieldGridClass = variant === "compact" ? "grid grid-cols-1 gap-4" : "grid grid-cols-1 sm:grid-cols-2 gap-4";
  const submitLabel = variant === "compact" ? t("contactForm.submitCompact") : t("contactForm.submitFull");
  const caseDetailsLabel = variant === "compact" ? t("contactForm.caseDetailsLabelCompact") : t("contactForm.caseDetailsLabelFull");

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid={`${testIdPrefix}-lead-form`} noValidate>
      <div className={fieldGridClass}>
        <FormField id={`${testIdPrefix}-full-name`} label={t("contactForm.fullNameLabel")} error={errors.full_name}>
          <Input
            id={`${testIdPrefix}-full-name`}
            value={form.full_name}
            onChange={setField("full_name")}
            placeholder={t("contactForm.fullNamePlaceholder")}
            className="mt-1.5 bg-white border-slate-300 focus-visible:ring-[color:var(--gold-500)]"
            data-testid={`${testIdPrefix}-full-name-input`}
            aria-invalid={!!errors.full_name}
            aria-describedby={errors.full_name ? `${testIdPrefix}-full-name-error` : undefined}
          />
        </FormField>

        <FormField id={`${testIdPrefix}-phone`} label={t("contactForm.phoneLabel")} error={errors.phone}>
          <Input
            id={`${testIdPrefix}-phone`}
            value={form.phone}
            onChange={setField("phone")}
            placeholder={t("contactForm.phonePlaceholder")}
            className="mt-1.5 bg-white border-slate-300 focus-visible:ring-[color:var(--gold-500)]"
            data-testid={`${testIdPrefix}-phone-input`}
            aria-invalid={!!errors.phone}
          />
        </FormField>
      </div>

      <div className={fieldGridClass}>
        <FormField id={`${testIdPrefix}-email`} label={t("contactForm.emailLabel")} error={errors.email}>
          <Input
            id={`${testIdPrefix}-email`}
            type="email"
            value={form.email}
            onChange={setField("email")}
            placeholder={t("contactForm.emailPlaceholder")}
            className="mt-1.5 bg-white border-slate-300 focus-visible:ring-[color:var(--gold-500)]"
            data-testid={`${testIdPrefix}-email-input`}
            aria-invalid={!!errors.email}
          />
        </FormField>

        <div>
          <Label htmlFor={`${testIdPrefix}-case-type`} className="text-slate-700">{t("contactForm.caseTypeLabel")}</Label>
          <Select value={form.case_type} onValueChange={setField("case_type")}>
            <SelectTrigger
              id={`${testIdPrefix}-case-type`}
              className="mt-1.5 bg-white border-slate-300 focus:ring-[color:var(--gold-500)]"
              data-testid={`${testIdPrefix}-case-type-select`}
            >
              <SelectValue placeholder={t("contactForm.caseTypePlaceholder")} />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {CASE_TYPE_OPTIONS.map((opt) => (
                <SelectItem key={opt} value={opt}>{t(`contactForm.caseTypeOptions.${opt}`, opt)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.case_type && <p className="mt-1 text-xs text-red-600">{errors.case_type}</p>}
        </div>
      </div>

      <FormField id={`${testIdPrefix}-message`} label={caseDetailsLabel} error={errors.message}>
        <Textarea
          id={`${testIdPrefix}-message`}
          value={form.message}
          onChange={setField("message")}
          placeholder={t("contactForm.caseDetailsPlaceholder")}
          rows={variant === "compact" ? 3 : 5}
          className="mt-1.5 bg-white border-slate-300 focus-visible:ring-[color:var(--gold-500)]"
          data-testid={`${testIdPrefix}-case-details-textarea`}
          aria-invalid={!!errors.message}
        />
      </FormField>

      {serverError && (
        <p className="text-sm text-red-600" data-testid={`${testIdPrefix}-error-message`}>{serverError}</p>
      )}

      <Button
        type="submit"
        disabled={submitting}
        className="w-full bg-[color:var(--gold-500)] text-[color:var(--navy-950)] hover:bg-[color:var(--gold-400)] font-semibold h-11"
        data-testid={`${testIdPrefix}-submit-button`}
      >
        {submitting ? (
          <><Loader2 className="h-4 w-4 animate-spin" /> {t("contactForm.submitting")}</>
        ) : (
          submitLabel
        )}
      </Button>
      <p className="text-xs text-slate-500 text-center">
        {t("contactForm.disclaimer")}
      </p>
      <p className="text-[11px] text-slate-400 text-center">
        {t("contactForm.recaptchaPrefix")}{" "}
        <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-slate-500">
          {t("contactForm.recaptchaPrivacy")}
        </a>{" "}
        {t("contactForm.recaptchaAnd")}{" "}
        <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-slate-500">
          {t("contactForm.recaptchaTerms")}
        </a>{" "}
        {t("contactForm.recaptchaSuffix")}
      </p>
    </form>
  );
};
